/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Form, Button, Modal, Checkbox } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import CloseableModal from 'components/common/CloseableModal/CloseableModal';
import {
  RawEditField,
  SpecialEditField,
} from 'components/modules/style-rules/components/StyleRuleSettingsItem/components';
import useStyleRuleConfigurations from 'components/modules/style-rules/hooks/useStyleRuleConfigurations';
import { getStyleRuleConfiguration } from 'components/modules/style-rules/services/configuration';
import {
  VIEW_TEMPLATE_MODE,
  APPLY_TEMPLATE_MODE,
  TEMPLATE_MODES,
} from 'components/modules/theme-templates/constants';
import { ThemeTemplatePropTypes } from 'components/modules/theme-templates/types';
import { DismissibleMessage } from 'components/modules/utils/components';
import ErrorHandler, { getErrorMessage } from 'libs/errors/errors';

import {
  getModeCtaCallback,
  createTemplateTagOptions,
  createOverrideOptions,
  validateTemplate,
} from './ThemeModalTemplate.utils';
import { MODE_MODAL_TITLES, MODE_CTA_TITLES } from './ThemeTemplateModal.constants';
import ThemeTemplateTypeDropdown from '../ThemeTemplateTypeDropdown';

import './ThemeTemplateModal.scss';

const propTypes = PropTypes.shape({
  mode: PropTypes.oneOf(TEMPLATE_MODES).isRequired,
  open: PropTypes.bool.isRequired,
  template: ThemeTemplatePropTypes,
  onEditTemplate: PropTypes.func,
  onApplyTemplate: PropTypes.func,
  onClose: PropTypes.func.isRequired,
});

const defaultProps = {
  template: null,
  onEditTemplate: () => {},
  onApplyTemplate: () => {},
};

function ThemeTemplateModal({ mode, open, template, onEditTemplate, onApplyTemplate, onClose }) {
  const {
    name: initialName = '',
    description: initialDescription = '',
    type: initialType = '',
    tags: initialTags = [],
    styleRules: initialStyleRules = [],
  } = template || {};

  const { styleRuleConfigurations } = useStyleRuleConfigurations();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [type, setType] = useState(initialType);
  const [tags, setTags] = useState(initialTags);
  const [styleRules, setStyleRules] = useState(initialStyleRules);
  const [options, setOptions] = useState({ reset: false });
  const [overrides, setOverrides] = useState(createOverrideOptions(initialStyleRules));

  function onResetTemplate() {
    setName(initialName);
    setDescription(initialDescription);
    setType(initialType);
    setTags(initialTags);
    setStyleRules(initialStyleRules);
  }

  function onResetError() {
    setErrors([]);
  }

  async function onAction(callback) {
    try {
      setLoading(true);

      const templateErrors = validateTemplate({ name, styleRules, type });

      if (!_isEmpty(templateErrors)) {
        setErrors(templateErrors);
        return;
      }

      await callback({
        ...template,
        name,
        description,
        tags,
        type,
        styleRules,
        options,
        overrides,
      });
    } catch (e) {
      ErrorHandler.capture(e);
      setErrors([getErrorMessage(e)]);
    } finally {
      setLoading(false);
    }
  }

  async function onEditTemplateInternal() {
    return onAction(onEditTemplate);
  }

  function onApplyTemplateInternal() {
    return onAction(onApplyTemplate);
  }

  function onTemplateNameChange(e, { value }) {
    setName(value);
  }

  function onTemplateDescriptionChange(e, { value }) {
    setDescription(value);
  }

  function onTemplateTypeChange(e, { value }) {
    setType(value);
  }

  function onTemplateTagsChange(e, { value }) {
    setTags(value);
  }

  function onOptionsChange(nextOptions) {
    setOptions((prevOptions) => ({
      ...prevOptions,
      ...nextOptions,
    }));
  }

  function onOverrideChange(overrideName, overrideValue) {
    setOverrides((prevOverrides) => ({
      ...prevOverrides,
      [overrideName]: overrideValue,
    }));
  }

  function onUpdateStyleRule(index, { name: ruleName, value: ruleValue }) {
    setStyleRules((prevStyleRules) => [
      ...prevStyleRules.slice(0, index),
      {
        ...prevStyleRules[index],
        name: _isNil(ruleName) ? prevStyleRules[index].name : ruleName,
        value: _isNil(ruleValue) ? prevStyleRules[index].value : ruleValue,
      },
      ...prevStyleRules.slice(index + 1),
    ]);
  }

  function onDeleteStyleRule(index) {
    setStyleRules((prevStyleRules) => [
      ...prevStyleRules.slice(0, index),
      ...prevStyleRules.slice(index + 1),
    ]);
  }

  function onAddStyleRule() {
    setStyleRules((prevStyleRules) => [...prevStyleRules, { name: '', value: '' }]);
  }

  useEffect(onResetTemplate, [open]);

  const modeCtaCallback = getModeCtaCallback(mode, {
    onEditTemplate: onEditTemplateInternal,
    onApplyTemplate: onApplyTemplateInternal,
    onClose,
  });
  const tagOptions = createTemplateTagOptions(tags);
  const editingDisabled = mode === VIEW_TEMPLATE_MODE || mode === APPLY_TEMPLATE_MODE;

  return (
    <CloseableModal
      open={open}
      onClose={onClose}
      header={MODE_MODAL_TITLES[mode]}
      className="theme-template-modal"
    >
      <Modal.Content>
        <Form>
          <Form.Input
            label="Name *"
            id="templateName"
            placeholder="Template Name"
            value={name}
            onChange={onTemplateNameChange}
            disabled={editingDisabled}
          />
          <Form.TextArea
            label="Description"
            id="templateDescription"
            placeholder="Template Description"
            value={description}
            onChange={onTemplateDescriptionChange}
            disabled={editingDisabled}
          />
          <Form.Dropdown
            label="Tags"
            id="templateTags"
            placeholder="Select tags"
            fluid
            multiple
            selection
            allowAdditions
            search
            options={tagOptions}
            value={tags}
            onChange={onTemplateTagsChange}
            disabled={editingDisabled}
          />
          <div className="field">
            <label htmlFor="templateStyleRules">Template Type *</label>
            <ThemeTemplateTypeDropdown
              id="templateStyleRules"
              value={type}
              onChange={onTemplateTypeChange}
              disabled={editingDisabled}
              clearable={false}
            />
          </div>
          <label htmlFor="templateStyleRules">Style Rules *</label>
          {styleRules.map(({ name: ruleName, value: ruleValue }, index) => {
            const styleRuleConfiguration = getStyleRuleConfiguration(
              styleRuleConfigurations,
              ruleName,
            ) || {
              name: ruleName,
              defaultValue: 'styleRuleName',
            };

            return (
              <Form.Group key={index} className="style-rule-group">
                <RawEditField
                  styleRuleConfiguration={styleRuleConfiguration}
                  value={ruleName || ''}
                  onChange={(e, { value }) => {
                    onUpdateStyleRule(index, { name: value || '' });
                  }}
                  onResultSelect={(e, { value }) => {
                    onUpdateStyleRule(index, { name: (value || '').replace(/@/g, '') });
                  }}
                  disabled={editingDisabled}
                />
                <RawEditField
                  styleRuleConfiguration={styleRuleConfiguration}
                  value={ruleValue || ''}
                  onChange={(e, { value }) => {
                    onUpdateStyleRule(index, { value });
                  }}
                  disabled={editingDisabled}
                />

                <SpecialEditField
                  styleRuleConfiguration={styleRuleConfiguration}
                  value={ruleValue || ''}
                  onChange={(e, { value }) => {
                    onUpdateStyleRule(index, { value });
                  }}
                  placeholder="10px"
                  disabled={editingDisabled}
                />

                {!editingDisabled && (
                  <Button
                    basic
                    onClick={() => onDeleteStyleRule(index)}
                    icon="trash"
                    color="red"
                    className="delete-button"
                  />
                )}
                {mode === APPLY_TEMPLATE_MODE && (
                  <Checkbox
                    toggle
                    checked={overrides[ruleName]}
                    onChange={(e, { checked }) => onOverrideChange(ruleName, checked)}
                    className="apply-template"
                  />
                )}
              </Form.Group>
            );
          })}
          {!editingDisabled && (
            <Button
              className="action-button add-style-rule"
              onClick={onAddStyleRule}
              icon="plus"
              content="Add Style Rule"
            />
          )}
          {mode === APPLY_TEMPLATE_MODE && (
            <div className="apply-template-options">
              <div className="label">Apply Template Options</div>
              <Checkbox
                toggle
                label="Reset Website Style Rules"
                defaultChecked={false}
                checked={options.reset}
                onChange={(e, { checked }) => onOptionsChange({ reset: checked })}
              />
            </div>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        {!_isEmpty(errors) && (
          <DismissibleMessage
            header="Theme Template Error"
            list={errors}
            delay={8}
            initialVisible
            error
            onDismiss={onResetError}
          />
        )}
        {mode !== VIEW_TEMPLATE_MODE && (
          <Button basic onClick={onClose} className="secondary-action" disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          basic
          onClick={modeCtaCallback}
          className="primary-action"
          disabled={loading}
          loading={loading}
        >
          {MODE_CTA_TITLES[mode]}
        </Button>
      </Modal.Actions>
    </CloseableModal>
  );
}

ThemeTemplateModal.propTypes = propTypes;
ThemeTemplateModal.defaultProps = defaultProps;

export default ThemeTemplateModal;
